/**
 * DeepLinkSelect (FEAT-DEEPLINK-001) — a dropdown of the tenant's Deep Link
 * Library entries, for places that model a "URL type" choice (e.g. WhatsApp /
 * In-App buttons) rather than a free-text URL. On selection it calls
 * `onPick(entry)` with the full library row; the host decides which field to use.
 *
 *   <DeepLinkSelect value={button.url} onPick={(e) => set('url', e.web_fallback_url || e.android_link)} />
 */
import React, { useEffect, useState } from 'react';
import { Select, Text, Spinner, HStack } from '@chakra-ui/react';
import deepLinksApi from '../../api/deepLinks';

export default function DeepLinkSelect({ value, onPick, size = 'sm', isDisabled = false, placeholder = 'Select a deep link…' }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError('');
      try {
        const res = await deepLinksApi.list({ status_id: 1, limit: 200 });
        if (!cancelled) setRows(res?.data?.data || []);
      } catch (e) {
        if (!cancelled) setError('Could not load the Deep Link Library');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Match the current value back to an entry (so an existing selection shows).
  const selectedId = (() => {
    const hit = rows.find((r) => [r.web_fallback_url, r.android_link, r.ios_link].includes(value));
    return hit ? String(hit.id) : '';
  })();

  if (loading) return <HStack color="gray.500" fontSize="sm"><Spinner size="xs" /><Text>Loading deep links…</Text></HStack>;
  if (error) return <Text fontSize="sm" color="red.400">{error}</Text>;
  if (!rows.length) return <Text fontSize="sm" color="gray.500">No deep links yet — add some in Content → Deep Link Library.</Text>;

  return (
    <Select
      size={size}
      isDisabled={isDisabled}
      placeholder={placeholder}
      value={selectedId}
      onChange={(e) => {
        const entry = rows.find((r) => String(r.id) === e.target.value);
        if (entry && onPick) onPick(entry);
      }}
    >
      {rows.map((r) => (
        <option key={r.id} value={r.id}>
          {r.name}{r.category ? ` — ${r.category}` : ''}
        </option>
      ))}
    </Select>
  );
}
